// src/components/suggestions/AISuggestionCard.tsx

import React from 'react';
import { ImmediateSuggestion, SuggestionType } from '../../types/suggestion';
import { RefreshCw, DollarSign, Clock, Coffee, Star } from '../common/icons';

interface AISuggestionCardProps {
  suggestions: ImmediateSuggestion[];
  overallReason?: string; // 新增：整體規劃說明
  isLoading: boolean;
  error: string | null;
  onSelectSuggestion: (suggestion: ImmediateSuggestion) => void;
  onRefresh: () => void;
}

const AISuggestionCard: React.FC<AISuggestionCardProps> = ({
  suggestions,
  overallReason,
  isLoading,
  error,
  onSelectSuggestion,
  onRefresh
}) => {
  // 格式化時間顯示
  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 
      ? `${hours}h ${remainingMinutes}m` 
      : `${hours}h`;
  };

  // Icon mapping for suggestion types
  const getSuggestionIcon = (type: SuggestionType) => {
    switch (type) {
      case 'task':
        return DollarSign; // Work tasks
      case 'life_activity':
        return Clock; // Life activities  
      case 'rest':
        return Coffee; // Rest suggestions
      default:
        return Star; // Default
    }
  };

  return (
    <div className="ai-guidance-card" style={{ marginTop: '12px' }}>
        {/* AI Header - 參考設計檔案 */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-4">
            {/* 48x48 AI 頭像 */}
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#667EEA" strokeWidth="2">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            {/* 標題 */}
            <div>
              <div className="text-sm font-semibold text-gray-600">
                Suggested for You
              </div>
            </div>
          </div>

          {/* 右側：刷新按鈕 */}
          {!isLoading && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onRefresh) {
                  onRefresh();
                }
              }}
              className="p-2 hover:bg-gray-50 rounded-md transition-colors duration-150"
              disabled={isLoading}
              aria-label="Refresh suggestions"
            >
              <RefreshCw 
                size={16} 
                className="text-gray-500 hover:text-gray-700" 
              />
            </button>
          )}
        </div>

        {/* 載入狀態 - 保持與完整狀態相同的寬度 */}
        {isLoading && (
          <div className="w-full">
            {/* 模擬描述文字區域 */}
            <div className="mb-6">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-3 w-full"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-3 w-5/6"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse w-4/5"></div>
            </div>
            {/* Loading 指示器 */}
            <div className="flex flex-col items-center mb-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-sky-600 border-t-transparent mb-3"></div>
              <p className="text-gray-600 text-base text-center">Analyzing your schedule...</p>
            </div>
            {/* 模擬建議按鈕區域 */}
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
              <div className="h-12 bg-gray-200 rounded-full animate-pulse flex-1 min-w-48"></div>
              <div className="h-12 bg-gray-200 rounded-full animate-pulse flex-1 min-w-48"></div>
              <div className="h-12 bg-gray-200 rounded-full animate-pulse flex-1 min-w-48"></div>
            </div>
          </div>
        )}

        {/* 錯誤狀態 - 保持與完整狀態相似的高度 */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center flex-1 py-8">
            <div className="text-red-500 mb-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="m15 9-6 6"/>
                <path d="m9 9 6 6"/>
              </svg>
            </div>
            <p className="text-red-600 text-base mb-6 max-w-md text-center">{error}</p>
            <button 
              onClick={onRefresh}
              className="px-6 py-2 text-sky-600 hover:text-sky-800 font-medium border border-sky-600 rounded-full hover:bg-sky-50 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {/* 建議展示 - 完全仿照截圖的極簡設計，響應式優化 */}
        {!isLoading && !error && suggestions.length > 0 && (
          <div>
            {/* AI 整體分析描述 - 參考設計檔案 */}
            {overallReason && (
              <div className="mb-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {overallReason}
                </p>
              </div>
            )}

            {/* Suggestion Chips - 參考設計檔案：桌面版水平，手機版垂直 */}
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap items-start">
              {suggestions.map((suggestion) => {
                const IconComponent = getSuggestionIcon(suggestion.type);
                
                return (
                  <button
                    key={suggestion.id}
                    onClick={() => onSelectSuggestion(suggestion)}
                    className="ai-chip justify-start w-full md:w-auto"
                  >
                    <IconComponent size={16} />
                    <span className="flex-1 text-left">{suggestion.title}</span>
                    <span className="text-sm opacity-75 flex-shrink-0">({formatDuration(suggestion.estimatedDuration)})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 無建議狀態 - 保持與完整狀態相似的高度 */}
        {!isLoading && !error && suggestions.length === 0 && (
          <div className="flex flex-col items-center justify-center flex-1 py-8">
            <div className="text-gray-400 mb-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11H1l6-6 6 6"/>
                <path d="M20 12l-6 6-6-6"/>
              </svg>
            </div>
            <p className="text-gray-600 text-base mb-6 max-w-md text-center">
              No suitable suggestions found for this time window
            </p>
            <button 
              onClick={onRefresh}
              className="px-6 py-2 text-sky-600 hover:text-sky-800 font-medium border border-sky-600 rounded-full hover:bg-sky-50 transition-colors"
            >
              Try different parameters
            </button>
          </div>
        )}
    </div>
  );
};

export default AISuggestionCard;